import React, { createContext, useState, ReactNode, useContext, useEffect } from 'react';
import { getAuth, User, signOut } from 'firebase/auth';
import {Chat, Activity, Group, Workspace, UnreadChat, WorkspaceMember, Ping, SidebarItem} from "../lib/types";
import { getFunctions, httpsCallable } from "firebase/functions";
import {getRef, removeTokenFromServer} from "../lib/database";
import {get, onValue, query, orderByChild, startAt, set, ref} from "firebase/database";
import {getItem, removeAll, setItem} from "../lib/localStorage";
import classNames from "classnames";
import {processUserAvatar} from "../lib/utils/files";
import {formatName} from "../lib/utils/formatting";
import {clearMessagingCache, initMessaging} from "../lib/utils/messaging";
import {isGeneratorFunction} from "node:util/types";

interface UserContextType {
  user: User | null;
  activeWorkspace: Workspace | null;
  setUser: (user: User | null) => void;
  groups: Group[];
  activity: Activity;
  setActiveWorkspace: (workspace: Workspace | null) => void;
  fetchGroups: () => void;
  unreadChats: UnreadChat[];
  activeChatId: string;
  setActiveChatId: (id: string) => void;
  getMember: (id: string) => WorkspaceMember | null;
  pings: Ping[];
  sidebar: SidebarItem[];
  getSidebarItemName: (item: SidebarItem, withAvatars?: boolean) => JSX.Element;
  fetchMySidebar: () => void
}

export const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);
  const [activeChatId, setActiveChatId] = useState<string>('');

  const [groups, setGroups] = useState<Group[]>(getItem('groups') || []);
  const [lastGroupFetchTimestamp, setLastGroupFetchTimestamp] = useState<number>(getItem('lastGroupFetchTimestamp') || 0);
  const [unreadChats, setUnreadChats] = useState<UnreadChat[]>([]);
  const [activeWorkspace, setActiveWorkspaceState] = useState<Workspace | null>(null);
  const [lastChatView, setLastChatView] = useState<number>(getItem(`lastChatView_${user?.uid}`) || 0);
  const [lastGroupView, setLastGroupView] = useState<number>(getItem(`lastGroupView_${user?.uid}`) || 0);
  const [me, setMe] = useState<WorkspaceMember | null>(null);
  const [activity, setActivity] = useState<Activity>({
    chats: {},
    groups: {},
    sidebarItems: {},
    pings: {
      lastView: getItem(`lastPingView`) || 0,
    },
  });

  const [pings, setPings] = useState<Ping[]>([]);
  const [sidebar, setSidebar] = useState< SidebarItem[]>(getItem('sidebar') || []);
  const fetchMySidebar = async () => {
    if (user && activeWorkspace) {
      const functions = getFunctions();
      const getMySidebar = httpsCallable<{ workspaceId: string }, { sidebar: { [key: string]: SidebarItem } }>(functions, 'loadMySidebar');
      try {
        const result = await getMySidebar({ workspaceId: activeWorkspace.id });
        const sidebar = result.data.sidebar;
        setSidebar(Object.values(sidebar).sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp));
      } catch (error) {
        console.error('Error fetching user sidebar:', error);
      }
    } else {
      setSidebar([]);
    }
  }
  const fetchGroups = async () => {
    if (user && activeWorkspace) {
      const now = Date.now();
      setItem('lastGroupFetchTimestamp', now);
      setLastGroupFetchTimestamp(now);
      const functions = getFunctions();
      const getUserGroups = httpsCallable<unknown, { groups: { [key: string]: Group } }>(functions, 'getUserGroups');
      try {
        const result = await getUserGroups();
        fetchMySidebar();
        const groups = Object.values(result.data.groups).filter(group => {
          return group.workspaceId === activeWorkspace?.id;
        }) as Group[];
        setGroups(groups);
        if(groups.length) {
          setItem('groups', groups);
        }
      } catch (error) {
        console.error('Error fetching user groups:', error);
      }
    } else {
      setGroups(getItem("groups") || []);
    }
  };

  const setUser = async (user: User | null) => {
    setUserState(user);
  };

  useEffect(() => {
    const me = getMember(user?.uid || '')
    if (me?.id) {
      initMessaging(me);
    }
    if(me?.id && user?.photoURL && !me.avatar) {
      //download avatar and upload to firebase
      const firebaseAvatarUrl = processUserAvatar(user);
      if(firebaseAvatarUrl) {
        set(getRef(`workspaces/${activeWorkspace?.id}/members/${user.uid}/avatar`), firebaseAvatarUrl);
      }
    }
  }, [activeWorkspace, user]);
  const setActiveWorkspace = async (workspace: Workspace | null) => {
    if (workspace) {
      const workspaceMembersRef = getRef('workspaces/' + workspace.id + '/members');
      onValue(workspaceMembersRef, async (snapshot) => {
        const data = snapshot.val();
        workspace.members = data || {};
        setActiveWorkspaceState(workspace);
      }, (error) => {
        console.error(error);
      });
    } else {
      setActiveWorkspaceState(null);
    }
  };

  useEffect(() => {
    if (user && activeWorkspace) {
      let unsubscribeChatActivity: any;
      let unsubscribeGroupActivity: any;
      let unsubscribePings: any;
      let unsubscribePingActivity: any;
      let unsubscribeSidebarItemsActivity: any;
      // Pings Listener
      const pingsRef = getRef('userPings/' + user.uid);
      unsubscribePings = onValue(pingsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const pings = Object.values(data) as Ping[];
          pings.sort((a, b) => b.timestamp - a.timestamp);
          setPings(pings);
        }
      });

      // Ping Activity Listener
      const pingActivityRef = getRef(`activity/${user.uid}/pings/lastView`);
      unsubscribePingActivity = onValue(pingActivityRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setItem(`lastPingView`, data);
          setActivity((prevActivity) => ({
            ...prevActivity,
            pings: {
              lastView: data,
            }
          }));
        }
      });

      const sidebarActivityRef = getRef(`activity/${user.uid}/sidebars`);
      unsubscribeSidebarItemsActivity = onValue(sidebarActivityRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const sidebarItems = { ...activity.sidebarItems, ...data };
          setActivity((prevActivity) => ({
            ...prevActivity,
            sidebarItems,
          }));
        }
      });
      // Chat Activity Listener
      const chatActivityRef = query(
        getRef(`activity/${user.uid}/chats`),
        orderByChild("lastView"),
        startAt(lastChatView + 1)
      );
      unsubscribeChatActivity = onValue(chatActivityRef, (snapshot) => {
        const data = snapshot.val();

        if (data) {
          const updatedChats = { ...activity.chats, ...data };
          // setItem(`lastChatView_${user.uid}`, Math.max(...Object.values(data).map((chat: any) => chat.lastView || 0)));
          setLastChatView(Math.max(...Object.values(data).map((chat: any) => chat.lastView || 0)));
          setActivity((prevActivity) => ({
            ...prevActivity,
            chats: updatedChats,
          }));
        }
      });

      // Group Activity Listener
      const groupActivityRef = query(
        getRef(`activity/${user.uid}/groups`),
      );
      unsubscribeGroupActivity = onValue(groupActivityRef, (snapshot) => {
        const data = snapshot.val();

        if (data) {
          const updatedGroups = { ...activity.groups, ...data };
          // setItem(`lastGroupView_${user.uid}`, Math.max(...Object.values(data).map((group: any) => group.lastView || 0)));
          setLastGroupView(Math.max(...Object.values(data).map((group: any) => group.lastView || 0)));
          setActivity((prevActivity) => ({
            ...prevActivity,
            groups: updatedGroups,
          }));
        }
      });

      return () => {
        if (unsubscribePingActivity) unsubscribePingActivity();
        if (unsubscribePings) unsubscribePings();
        if (unsubscribeChatActivity) unsubscribeChatActivity();
        if (unsubscribeGroupActivity) unsubscribeGroupActivity();
        if (unsubscribeSidebarItemsActivity) unsubscribeSidebarItemsActivity();
      };
    }
  }, [user, activeWorkspace, lastChatView, lastGroupView]);

  useEffect(() => {
    let unsusbscribeUnreadChats: any;
    if(user && activeWorkspace)  {
      const unreadChatsRef = getRef('unread/'+user.uid);
      unsusbscribeUnreadChats = onValue(unreadChatsRef, (snapshot) => {
        const data = snapshot.val();
        if(!data) {
          setUnreadChats([]);
        } else {
          setUnreadChats(Object.values(data).map((i: any) => {
            if(!i.groupPermissions) {
              i.groupPermissions = {};
            }
            if(!i.userPermissions) {
              i.userPermissions = {};
            }
            return i as UnreadChat;
          }));
        }
      });
    }
    return () => {
      if(unsusbscribeUnreadChats) unsusbscribeUnreadChats();
    }
  }, [user, activeWorkspace]);

  useEffect(() => {
    if (user && activeWorkspace) {
      let missingGroups = false;
      unreadChats.forEach((unreadChat) => {
        const unreadChatGroups = Object.keys(unreadChat.groupPermissions || {});
        if (unreadChatGroups.some((groupId) => !groups.find((group) => group.id === groupId))) {
          missingGroups = true;
        }
      });
      if (groups.length === 0 || missingGroups || lastGroupFetchTimestamp < Date.now() - 5000) {
        fetchGroups();
      }
      fetchMySidebar();
    }
  }, [user, activeWorkspace, unreadChats]);

  const getMember = (id: string) => {
    if (activeWorkspace) {
      if (activeWorkspace.members[id]) {
        return activeWorkspace.members[id];
      }
      return null;
    } else {
      return null;
    }
  };

  const getSidebarItemNames = (item: SidebarItem, withAvatars=false) => {
    const names: (string | ReactNode)[] = [];
    if (item.meaningfulGroups) {
      item.meaningfulGroups.forEach(groupId => {
        const group = groups.find(group => group.id === groupId) as Group;
        if (group) {
          names.push(group.emoji +" "+group.name);
        }
      })
    }
    if (item.meaningfulUsers) {
      item.meaningfulUsers.forEach(uid => {
        const member = getMember(uid) as WorkspaceMember;
        const onlyMe = member.id === user?.uid && item.meaningfulUsers?.length === 1 && !item.meaningfulGroups?.length;
        if (member && (member.id !== user?.uid || onlyMe)) {
          names.push(<span className={"inline-flex gap-1 items-center justify-start"}>
            {withAvatars && <img src={member?.avatar} alt={member?.displayName || ""}
                                 onError={(e) => {e.currentTarget.onerror=null; e.currentTarget.src='/avatar.png'}}
                                 className="w-6 rounded-md"/>}
            {formatName(member.displayName)}
          </span>);
        }
      })
    }
    return names;
  }

  const getSidebarItemName = (item: SidebarItem, withAvatars=false) => {
    const names = getSidebarItemNames(item, withAvatars);

    return <div className="names flex gap-1 flex-nowrap line-clamp-1 items-center">
      {names.map((item, idx) => {
        return <span className={"flex-shrink-0"}>{idx > 0 && ", "} {item}</span>
      })}
    </div>
  }

  return (
    <UserContext.Provider value={{ fetchMySidebar, getSidebarItemName, sidebar, pings, getMember, activeChatId, setActiveChatId, fetchGroups, user, unreadChats, activity, groups, setUser, activeWorkspace, setActiveWorkspace }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

export const logout = async () => {
  const auth = getAuth();
  console.log("Logging out...", auth.currentUser);

  if (auth.currentUser) {
    await clearMessagingCache(auth.currentUser);
  }
  removeAll();
  signOut(auth);
};
